import React from 'react';
import styled from 'styled-components';
import { match } from 'react-router';

import ActiveState, { ActiveStateProps } from './states/Active';
import BannedState, { IBannedCallingState } from './states/Banned';
import ErrorState from './states/Error';
import DefaultState, { DefaultStateProps } from './states/Default';
import CallingState, { CallingStateProps } from './states/Calling';
import States from './States';
import AudioPlayer from '../AudioPlayer';
import {
  HOLD_SOUND,
  INCOMING_SOUND,
  OUTGOING_SOUND,
  TRANSFER_SOUND,
} from '../../constants/sounds';
import { ValueOf } from '@utils/tsUtils';
import { ContactType } from './types';
import { GeneralManagerType } from 'types/types';
import { DepartmentsDropdownProps } from '@components/CRM/Dialer/DepartmentsDropdown/DepartmentsDropdown';
import { OperatorsDropdownProps } from '@components/CRM/Dialer/OperatorsDropdown/OperatorsDropdown';
import { VOX_STATES } from '@constants/vox';

const { Status: AudioStatus } = AudioPlayer;

interface DialerProps {
  className?: string;
  state: ValueOf<typeof States>;
  calls: Array<{ name?: string; number: string }> | null;
  contact: ContactType | null;
  departments: DepartmentsDropdownProps['departments'];
  error: string | null;
  forceShown: boolean;
  history?: any;
  isAuthorized: boolean;
  isDeanonymised: boolean;
  isHold: boolean;
  isInterception: boolean;
  isMaster: boolean;
  isRequestingHelp: boolean;
  isSupervising: boolean;
  isSupervisor: boolean;
  isTransfering: boolean;
  isVoteNeed: boolean;
  isAlfaTravelManager: boolean;
  location?: any;
  manualState: ValueOf<typeof VOX_STATES>;
  match?: match<{
    isExact: boolean;
    params: { chain_id?: string; ticket_id: string };
    path: string;
    url: string;
  }>;
  operator: {
    name: string;
    number: string;
  };
  operatorState: ValueOf<typeof States>;
  operators: OperatorsDropdownProps['operators'];
  serverError: string | null;
  volume: number;

  onAnswer: CallingStateProps['onSubmit'];
  onCall: DefaultStateProps['onCall'];
  onCancelHelpRequest: ActiveStateProps['onCancelHelpRequest'];
  onConnectSupervisor: ActiveStateProps['onConnectSupervisor'];
  onDeaninomize: ActiveStateProps['onDeaninomize'];
  onDepartmentsToggle: ActiveStateProps['onDepartmentsToggle'];
  onHangup: ActiveStateProps['onHangup'];
  onHold: ActiveStateProps['onHold'];
  onListen: DefaultStateProps['onListen'];
  onOpenCall: ActiveStateProps['onOpenCall'];
  onOperatorsToggle: OperatorsDropdownProps['onToggle'];
  onRecentCallsToggle: DefaultStateProps['onRecentCallsToggle'];
  onRequestHelp: ActiveStateProps['onRequestHelp'];
  onTone: ActiveStateProps['onTone'];
  onTransfer: ActiveStateProps['onTransfer'];
  onUnban: IBannedCallingState['onSubmit'];
  onWithdraw: ActiveStateProps['onWithdraw'];
  onStatusChange: DefaultStateProps['onStatusChange'];
}

class Dialer extends React.PureComponent<DialerProps> {
  static defaultProps = {
    calls: null,
    className: '',
    contact: null,
    isDeanonymised: false,
    isHold: false,
    isInterception: false,
    isMaster: false,
    isRequestingHelp: false,
    isSupervising: false,
    isSupervisor: false,
    isTransfering: false,
    isVoteNeed: false,
    isAlfaTravelManager: false,
    onAnswer: Function.prototype,
    onCall: Function.prototype,
    onCancelHelpRequest: Function.prototype,
    onConnectSupervisor: Function.prototype,
    onDeaninomize: Function.prototype,
    onDepartmentsToggle: Function.prototype,
    onHangup: Function.prototype,
    onHold: Function.prototype,
    onListen: Function.prototype,
    onOperatorsToggle: Function.prototype,
    onStatusChange: Function.prototype,
    onRecentCallsToggle: Function.prototype,
    onRequestHelp: Function.prototype,
    onTone: Function.prototype,
    onTransfer: Function.prototype,
    onUnban: Function.prototype,
    onWithdraw: Function.prototype,
    onOpenCall: Function.prototype,
    operators: null,
    state: States.OFFLINE,
    manualState: States.OFFLINE,
    error: null,
    serverError: null,
    volume: 100,
  };

  static isCalling(state) {
    return [
      VOX_STATES.INCOMING,
      VOX_STATES.IN_SERVICE,
      VOX_STATES.OUTGOING,
    ].includes(state);
  }

  static isBanned(state) {
    return VOX_STATES.BANNED === state;
  }

  render() {
    const {
      calls,
      contact,
      departments,
      isDeanonymised,
      isHold,
      isInterception,
      isMaster,
      isAuthorized,
      isRequestingHelp,
      isSupervising,
      isSupervisor,
      isTransfering,
      isVoteNeed,
      isAlfaTravelManager,
      onAnswer,
      onCall,
      onCancelHelpRequest,
      onConnectSupervisor,
      onDeaninomize,
      onDepartmentsToggle,
      onHangup,
      onHold,
      onListen,
      onOperatorsToggle,
      onStatusChange,
      onRecentCallsToggle,
      onRequestHelp,
      onTone,
      onTransfer,
      onUnban,
      onWithdraw,
      onOpenCall,
      operators,
      state,
      manualState,
      error,
      serverError,
      volume,
    } = this.props;

    if (error) {
      return <ErrorState error={error} />;
    }

    return (
      <StyledDialer>
        {!Dialer.isCalling(state) && ![VOX_STATES.BANNED].includes(state) && (
          <StyledDefaultState
            calls={calls}
            onCall={onCall}
            onListen={onListen}
            onOperatorsToggle={onOperatorsToggle}
            onStatusChange={onStatusChange}
            onRecentCallsToggle={onRecentCallsToggle}
            operators={operators}
            state={state}
            manualState={manualState}
            isAuthorized={isAuthorized}
            isAlfaTravelManager={isAlfaTravelManager}
          />
        )}
        {state === VOX_STATES.INCOMING && (
          <CallingState
            contact={contact}
            volume={volume}
            onSubmit={onAnswer}
            isMasterInstance={isMaster}
            isInterception={isInterception}
          />
        )}
        {state === VOX_STATES.IN_SERVICE && (
          <StyledActiveState
            state={state}
            operators={operators}
            isHold={isHold}
            isDeanonymised={isDeanonymised}
            isInterception={isInterception}
            isTransfering={isTransfering}
            isSupervising={isSupervising}
            isSupervisor={isSupervisor}
            isRequestingHelp={isRequestingHelp}
            isVoteNeed={isVoteNeed}
            serverError={serverError}
            contact={contact}
            onHangup={onHangup}
            onHold={onHold}
            onTone={onTone}
            onTransfer={onTransfer}
            onWithdraw={onWithdraw}
            onRequestHelp={onRequestHelp}
            onCancelHelpRequest={onCancelHelpRequest}
            onConnectSupervisor={onConnectSupervisor}
            onDeaninomize={onDeaninomize}
            departments={departments}
            onOperatorsToggle={onOperatorsToggle}
            onDepartmentsToggle={onDepartmentsToggle}
            onOpenCall={onOpenCall}
          />
        )}
        {state === VOX_STATES.OUTGOING && (
          <CallingState
            volume={volume}
            contact={contact}
            onSubmit={onHangup}
            isMasterInstance={isMaster}
            type={VOX_STATES.OUTGOING}
          />
        )}
        {state === VOX_STATES.BANNED && <BannedState onSubmit={onUnban} />}
        {isMaster && (
          <>
            <AudioPlayer
              loop
              autoLoad
              volume={volume}
              playStatus={isHold ? AudioStatus.PLAYING : AudioStatus.STOPPED}
              url={HOLD_SOUND}
            />
            <AudioPlayer
              loop
              autoLoad
              volume={volume}
              playStatus={AudioStatus.STOPPED}
              url={INCOMING_SOUND}
            />
            <AudioPlayer
              loop
              autoLoad
              volume={volume}
              playStatus={AudioStatus.STOPPED}
              url={OUTGOING_SOUND}
            />
            <AudioPlayer
              loop
              autoLoad
              volume={volume}
              playStatus={
                isTransfering ? AudioStatus.PLAYING : AudioStatus.STOPPED
              }
              url={TRANSFER_SOUND}
            />
          </>
        )}
      </StyledDialer>
    );
  }
}

const StyledDialer = styled.div`
  width: 100%;
  min-width: 960px;
  height: 65px;
  display: flex;
  font-size: 14px;
  align-items: center;
  background-color: #262729;
  justify-content: space-between;
  padding: 12px;
`;

const StyledActiveState = styled(ActiveState)`
  margin-left: 10px;
`;

const StyledDefaultState = styled(DefaultState)`
  margin: 0 10px;
`;

export default Dialer;
